import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { NewSelect } from "../..";
import { field, FilterContent, DisplayType, FilterDisplayEnum, NewFieldStyles, QuickEntry } from "../../../Definitions";

// TODO
import useSelectOptions from "../../_Components/useSelectOptions";
import { ValueType } from "../../../Definitions/valueTypes";

type QuickEntryModalLvl1Props = {
  quickEntryData: QuickEntry[];
  onQuickEntryDataChange: (data: QuickEntry[]) => void;
  operationsView?: boolean
};

export default function QuickEntryModalLvl1({quickEntryData, onQuickEntryDataChange, operationsView=false}:QuickEntryModalLvl1Props) {
  const {t} = useTranslation()
  const {groupOptions, feedcurveOptions, milestoneOptions, systemOptions} = useSelectOptions();
  const [selectFields, setSelectFields] = useState<field[]>([{ id: -1 }]);
  const mobileXs = useMediaQuery(`(max-width: 793px)`);

  // #region | Select Parameters
  const selectParameters: DisplayType[] = [
    // Number
    {label: "animal_number", id: 1, displayType: FilterDisplayEnum.range},
    
    // Group
    {label: "group", id: 2, displayType: FilterDisplayEnum.select, options: groupOptions.filter(option => option.id !== undefined) as { label: string; id: number }[]},
    
    // Feed Curve
    {label: "feedCurve", id: 3, displayType: FilterDisplayEnum.select, options: feedcurveOptions},
    
    // System
    {label: "system", id: 5, displayType: FilterDisplayEnum.select, options: systemOptions},
    
    // Location
    // {label: "location", id: 5, displayType: FilterDisplayEnum.select, options: locationOptions},
    
    // Milestones
    {label: "milestone", id: 6, displayType: FilterDisplayEnum.select, options: milestoneOptions},
    
    // Feed Balance
    // Possibility to set a feed balance % and select all pigs above/below this 
  ]

  const labelToKeyMap: Record<string, "animal_number" | "feedCurveId" | "animalGroupId" | "systemId" | "milestone_id"> = {
    animal_number: "animal_number",
    feedCurve: "feedCurveId",
    group: "animalGroupId",
    system: "systemId",
    milestone: "milestone_id"
  };

  const filterParamOptions = selectParameters.map((type) => ({
    label: t(`params.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));
  // #endregion

  // If quickEntryData already have values to show
  useEffect(() => {
    if (quickEntryData && quickEntryData.length > 0) {
      const updatedFields: field[] = quickEntryData.map((param) => {
        const label = Object.entries(labelToKeyMap).find(([_, key]) => key === param.key)?.[0];
        const id = selectParameters.find((p) => p.label === label)?.id ?? -1;
        return { id, value: param.value };
      });
      updatedFields.push({ id: -1 });
      setSelectFields(updatedFields);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickEntryData]);
  
  // Handle filter type change
  const handleFilterTypeChange = (index: number, id: number ) => {
    const updatedSelectFields = [...selectFields];
    if (updatedSelectFields[index].id === -1) {
      updatedSelectFields.push({ id: -1 });
    }
    updatedSelectFields[index] = { id: id, value: undefined };

    const selectedParameter = selectParameters.find((param) => param.id === id);
    if (selectedParameter) {
      const updatedFilterParameter = [...quickEntryData];
      const mappedKey = labelToKeyMap[selectedParameter.label];
      // console.log(mappedKey, selectedParameter.label)
      if (mappedKey) {
        updatedFilterParameter[index] = {
          key: mappedKey,
          value: []
        };
        onQuickEntryDataChange(updatedFilterParameter);
      }
    }
  setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, values: ValueType[]) => {
    const updatedFilterParameter = Array.isArray(quickEntryData) ? [...quickEntryData] : [];
  
    if (!updatedFilterParameter[index]) return;
  
    updatedFilterParameter[index].value = values;
    // console.log(values, updatedFilterParameter); 
    onQuickEntryDataChange(updatedFilterParameter);
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);
  
    const updatedFilterParameter = quickEntryData.filter((_, i) => i !== index);
  
    onQuickEntryDataChange(updatedFilterParameter);
  }, [onQuickEntryDataChange, quickEntryData, selectFields]);

  // console.log(quickEntryData);
  
  // #region | Rendered Fields
  const renderedFields = selectFields.map((field, index) => {
    // Remove already used options from optionslist
    const selectedIds = selectFields
    .filter((f, i) => i !== index && f.id !== undefined)
    .map((f) => f.id); 
    const availableOptions = filterParamOptions.filter((option) => !selectedIds.includes(option.id));

    return (
      <div key={field.id !== undefined ? `${field.id}-${index}` : index} style={{ 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        gap: "10px", 
        marginBottom: index === selectFields.length - 1 ? "0px" : "30px"
      }}>
        <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "end", justifyContent: "space-between" }}>
          <NewSelect
            placeholder={t("select", {defaultValue: "select"})}
            key={index+String(field.id)}
            options={availableOptions}
            onChange={(option) => {
              option?.id && handleFilterTypeChange(index, Number(option.id));
            }}
            fieldOverrides={{
              fieldWidth: mobileXs ? "250px" : "280px",
              fieldStyle: operationsView ? NewFieldStyles.greyDarkPrimary : NewFieldStyles.blueDarkSecondary
            }}
            valueOverrides={{
              defaultValue: filterParamOptions.find((parameter) => {return parameter.id === field.id})
            }}
          />
          {field.id !== -1 && ( <>
            <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
              <glyphs.Delete size="25px"/>
            </div>
          </> )}
        </div>
        {/* Content based on selected filter parameter */}
        {field.id !== -1 && (
          <div style={{    
            width: "100%",
            height: "fit-content",
            boxSizing: "border-box",
            backgroundColor: operationsView ? colors.greyPrimary : colors.greyLightSecondary,
            padding: mobileXs? "10px 5px" : "10px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <FilterContent 
              filter={field} 
              index={index} 
              filterParameterTypes={selectParameters} 
              handleFilterValueChange={(values) => {handleFilterValueChange(index, values)}}
              operationsView={operationsView}
            />
          </div>
        )}
      </div>
    )
  })
  // #endregion
  
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      boxSizing: "border-box"
    }}>
      {renderedFields}
    </div>
  )
}